import React, { useCallback, useState } from "react";
import ky from "ky";

import { Sheet } from "./Sheet";
import {
  Button,
  IconChevronRight,
  Skeleton,
  ProductCard as UIProductCard,
  type ProductCardProps as UIProductCardProps,
} from "@bared/ui";

import type { ProductCardContext } from "types/product";

import {
  ProductCardVisibilityObserver,
  ProductCardVisibilityObserverProps,
} from "~/components/ProductCardVisibilityObserver";

import { useCartAdd } from "~/hooks/useCart";
import { useWishlistToggle } from "~/hooks/useWishlist";
import { useSite } from "~/hooks/useSite";

import type {
  SiblingProductsResponse,
  SiblingProductsRequest,
} from "~/api/sibling-products";

export type ProductCardProps = ProductCardContext &
  Omit<
    UIProductCardProps,
    "isInWishlist" | "onWishlistToggle" | "isAddingToCart"
  > &
  Omit<ProductCardVisibilityObserverProps, "children"> & {
    enableWishlist?: boolean;
    enableAddToCart?: boolean;
    enableSheet?: boolean;
  };

export function ProductCard({
  index,
  initialInView,
  klaviyoItem,
  googleAnalyticsItem,
  firstAvailableVariantId,
  enableWishlist = true,
  enableAddToCart = false,
  enableSheet = true,
  ...props
}: ProductCardProps) {
  const { toggleWishlist, isInWishlist } = useWishlistToggle({
    klaviyoItem,
    googleAnalyticsItem,
    productHandle: props.handle,
  });

  const { isPending, addToCart } = useCartAdd();
  const site = useSite();

  const [isOpen, setIsOpen] = useState(false);
  const [siblingProducts, setSiblingProducts] = useState<
    SiblingProductsResponse["siblingProducts"] | null
  >(null);
  const [isSiblingProductsPending, setIsSiblingProductsPending] =
    useState<boolean>(false);

  const handleSheetToggle = useCallback(async () => {
    setIsOpen((prev) => !prev);

    // Only fetch on opening the sheet
    if (!isOpen) {
      try {
        setIsSiblingProductsPending(true);
        const { siblingProducts: response } = await ky
          .post("/api/sibling-products", {
            headers: { "X-Site-Handle": site.handle },
            json: {
              styleName: props.title,
            } satisfies SiblingProductsRequest,
          })
          .json<SiblingProductsResponse>();

        setSiblingProducts(response);
        setIsSiblingProductsPending(false);
      } catch (error) {
        console.error("Failed to fetch sibling products:", error);
      }
    }
  }, [isOpen, site.handle, props.title]);

  return (
    <ProductCardVisibilityObserver
      index={index}
      initialInView={initialInView}
      googleAnalyticsItem={googleAnalyticsItem}
    >
      {({ ref, onClick }) => (
        <div className="flex flex-col">
          <UIProductCard
            {...props}
            ref={ref}
            onClick={onClick}
            {...(enableWishlist
              ? { isInWishlist, onWishlistToggle: toggleWishlist }
              : {})}
            {...(enableAddToCart && !!firstAvailableVariantId
              ? {
                  isAddingToCart: isPending,
                  onAddToCart: async () => {
                    if (!firstAvailableVariantId) return;
                    await addToCart({ merchandiseId: firstAvailableVariantId });
                    if (props.onAddToCart) props.onAddToCart();
                  },
                }
              : {})}
          />

          {enableSheet && props.colourVariantCount > 1 && (
            <div className="px-micro">
              <Button
                variant="plain"
                className="flex items-center gap-micro hover:text-secondary"
                onClick={handleSheetToggle}
              >
                <p className="text-caption font-medium leading-caption underline">
                  {props.colourVariantCount} Colours
                </p>
                <IconChevronRight className="h-micro w-micro" />
              </Button>
              <Sheet
                isOpen={isOpen}
                onClose={handleSheetToggle}
                title={`${props.title} / ${props.colourVariantCount} Colours`}
                eventTitle="More colours"
                layout="condensed"
              >
                <div className="grid grid-cols-2 gap-x-pico gap-y-midi">
                  {isSiblingProductsPending
                    ? new Array(props.colourVariantCount)
                        .fill(true)
                        .map((_, index) => (
                          <Skeleton
                            className="flex flex-col space-y-mini"
                            key={`skeleton-${index}`}
                          >
                            <div className="aspect-[3000/3273] bg-canvas" />
                            <div className="ml-mini">
                              <div className="mb-micro h-[20px] w-[75%] bg-canvas" />
                              <div className="h-[20px] w-[50%] bg-canvas" />
                            </div>
                          </Skeleton>
                        ))
                    : !!siblingProducts &&
                      siblingProducts.length > 0 &&
                      siblingProducts.map((product, index) => (
                        <ProductCard
                          key={product.handle}
                          {...product}
                          index={index}
                          sizes="33vw"
                          enableWishlist={true}
                          enableAddToCart={false}
                          enableSheet={false}
                        />
                      ))}
                </div>

                <div className="px-mini py-midi">
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleSheetToggle}
                    className="w-full"
                  >
                    Continue Shopping
                  </Button>
                </div>
              </Sheet>
            </div>
          )}
        </div>
      )}
    </ProductCardVisibilityObserver>
  );
}
